import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import TopBar from "../components/TopBar";
import CefrBadge from "../components/CefrBadge";
import { CEFR_ORDER, CEFR_LABELS } from "../lib/cefr";
import {
  ArrowRight, ArrowUpRight, BookOpen, Ear, PencilSimpleLine, Books,
  ChatCircleDots, ChartLineUp, Sparkle, ClockCountdown, Quotes, Globe,
  CheckCircle, Waveform, Microphone,
} from "@phosphor-icons/react";

export default function Landing() {
  const { user } = useAuth();
  const isAuthed = user && user !== false;
  const startTo = isAuthed ? "/test" : "/register";
  const startLabel = isAuthed ? "Take the test" : "Take the free test";

  return (
    <div className="min-h-screen">
      <TopBar />

      <div className="max-w-7xl mx-auto px-6 pt-6 pb-16">
        {/* Overline strip */}
        <div className="flex items-center justify-between mb-8 px-2">
          <div className="overline flex items-center gap-2" data-testid="landing-overline">
            <Sparkle size={12} weight="fill" className="text-indigo" />
            Voxlingua · CEFR-mapped self-assessment
          </div>
          <div className="overline hidden md:block">Est. 80 min · A1–C2</div>
        </div>

        {/* ============ HERO ============ */}
        <section className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-7 neu rounded-3xl p-10 md:p-14 relative overflow-hidden">
            <h1 className="text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-foreground font-bold">
              How good is your{" "}
              <span className="font-display italic text-indigo font-medium">English</span>,<br />
              really?
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
              An 87-question diagnostic across grammar, vocabulary, reading,
              listening, writing and speaking. Score out of 100, CEFR level A1 to C2.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to={startTo}
                data-testid="landing-start-cta"
                className="neu-btn-primary group inline-flex items-center gap-2 px-8 py-4 text-base font-medium"
              >
                {startLabel}
                <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to={isAuthed ? "/dashboard" : "/login"}
                data-testid="landing-secondary-cta"
                className="neu-btn inline-flex items-center gap-2 text-sm px-6 py-3"
              >
                {isAuthed ? "Go to dashboard" : "I have an account"}
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 flex flex-col gap-6">
            {/* Sample result */}
            <div className="neu rounded-3xl p-8 flex-1">
              <div className="overline flex items-center gap-2">
                <Sparkle size={12} weight="fill" className="text-indigo" /> Sample result
              </div>
              <div className="mt-6 flex items-end gap-3">
                <div className="font-mono-fig text-7xl text-foreground leading-none">78</div>
                <div className="text-muted-foreground text-sm pb-2">/ 100</div>
              </div>
              <div className="mt-5">
                <CefrBadge level="B2" size="md" showLabel testId="landing-sample-badge" />
              </div>
              <p className="mt-5 text-sm text-foreground/80 leading-relaxed">
                Can interact fluently with native speakers and produce clear, detailed text on a wide range of subjects.
              </p>
            </div>

            {/* Stats trio */}
            <div className="neu rounded-3xl p-6">
              <div className="overline mb-4">By the numbers</div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { n: "87", l: "Questions" },
                  { n: "80", l: "Minutes" },
                  { n: "6", l: "Levels" },
                ].map((s) => (
                  <div key={s.l} className="neu-inset rounded-2xl px-3 py-4 text-center">
                    <div className="font-mono-fig text-2xl text-foreground">{s.n}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============ SKILLS ============ */}
        <section className="mt-16">
          <div className="flex items-end justify-between mb-8 px-2">
            <div>
              <div className="overline">01 — Six skills tested</div>
              <h2 className="text-3xl md:text-4xl mt-3 text-foreground font-bold tracking-tight">
                An honest picture. Not a marketing quiz.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <SkillCell icon={Books} name="Grammar" meta="15 questions" desc="Tenses, articles, conditionals and complex clauses." testId="landing-skill-grammar" />
            <SkillCell icon={ChatCircleDots} name="Vocabulary" meta="12 questions" desc="Everyday words to advanced idioms and collocations." testId="landing-skill-vocabulary" />
            <SkillCell icon={BookOpen} name="Reading" meta="7 parts · 37 questions" desc="Notices, matching, articles, gapped texts and word gaps — B1 Preliminary style." testId="landing-skill-reading" />
            <SkillCell icon={Ear} name="Listening" meta="4 parts · 21 questions" desc="Short clips, conversations, note-taking and an interview. All questions of a part on one page; each recording plays up to 3 times." testId="landing-skill-listening" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            <div className="lg:col-span-2 neu rounded-3xl p-8 flex flex-col sm:flex-row items-start gap-6" data-testid="landing-skill-writing">
              <div className="neu-sm w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 text-indigo">
                <PencilSimpleLine size={22} weight="duotone" />
              </div>
              <div className="flex-1">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className="text-2xl text-foreground font-bold">Writing</div>
                  <div className="overline">2 tasks · rule-based scoring</div>
                </div>
                <p className="mt-3 text-sm text-foreground/80 leading-relaxed">
                  A required email answering 4 notes, then an article or a story — about 100 words each.
                  Points are given for each part of the task you cover, plus range and accuracy. No AI in
                  the loop, no waiting for grades.
                </p>
              </div>
            </div>

            <SkillCell
              icon={Microphone}
              name="Speaking"
              meta="2 tasks · up to 3 recordings each"
              desc="Describe a picture, then answer a question about your life — about a minute each. You'll be asked to allow microphone access."
              testId="landing-skill-speaking"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 mt-6">
            <div className="neu rounded-3xl p-8 flex flex-col justify-between">
              <ChartLineUp size={26} weight="duotone" className="text-indigo" />
              <div>
                <div className="text-xl text-foreground font-bold mt-6">Every attempt saved</div>
                <div className="overline mt-2">Track your CEFR climb over time.</div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ CEFR LADDER ============ */}
        <section className="mt-16">
          <div className="mb-8 px-2">
            <div className="overline">02 — The CEFR scale</div>
            <h2 className="text-3xl md:text-4xl mt-3 text-foreground font-bold tracking-tight">
              From <span className="font-display italic text-indigo font-medium">A1</span> beginner to <span className="font-display italic text-indigo font-medium">C2</span> proficient.
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {CEFR_ORDER.map((lvl, i) => (
              <div key={lvl} className="neu rounded-3xl p-5 relative overflow-hidden">
                <CefrBadge level={lvl} size="sm" muted />
                <div className="text-lg text-foreground font-bold mt-4">{CEFR_LABELS[lvl]}</div>
                <div className="overline mt-1">Level {lvl}</div>
                <div className="numeral text-5xl mt-6 text-foreground/15">
                  0{i + 1}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ============ HOW IT WORKS ============ */}
        <section className="mt-16">
          <div className="mb-8 px-2">
            <div className="overline">03 — How it works</div>
            <h2 className="text-3xl md:text-4xl mt-3 text-foreground font-bold tracking-tight">
              Three quiet steps.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", title: "Sign up in seconds", desc: "Create a free account — email and password, nothing else.", icon: Globe },
              { n: "02", title: "Take the 80-minute test", desc: "Flow through six sections at your own pace with a visible timer.", icon: ClockCountdown },
              { n: "03", title: "Get your CEFR level", desc: "Per-skill breakdown, writing feedback and study tips.", icon: Waveform },
            ].map((s) => (
              <div key={s.n} className="neu rounded-3xl p-8">
                <div className="flex items-start justify-between">
                  <div className="neu-sm w-12 h-12 rounded-2xl flex items-center justify-center text-indigo">
                    <s.icon size={20} weight="duotone" />
                  </div>
                  <div className="overline">Step {s.n}</div>
                </div>
                <div className="text-xl text-foreground font-bold mt-6">{s.title}</div>
                <p className="text-sm text-foreground/75 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ============ TESTIMONIAL + CTA ============ */}
        <section className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5 neu rounded-3xl p-8 flex flex-col justify-between">
            <Quotes size={32} weight="fill" className="text-indigo" />
            <div>
              <p className="font-display italic text-3xl text-foreground leading-snug mt-4">
                &ldquo;Finally, a level test that feels honest — not a marketing quiz.&rdquo;
              </p>
              <div className="mt-6 flex items-center gap-3">
                <div className="neu-sm w-10 h-10 rounded-full flex items-center justify-center text-indigo font-mono-fig text-sm">A</div>
                <div>
                  <div className="text-sm text-foreground">Anaïs</div>
                  <div className="overline">B1 → B2 in 4 months</div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7 neu rounded-3xl p-10 md:p-14 relative overflow-hidden">
            <div className="overline flex items-center gap-2">
              <CheckCircle size={12} weight="duotone" /> Ready?
            </div>
            <h2 className="text-4xl md:text-5xl mt-4 text-foreground font-bold tracking-tight leading-tight">
              Find out where{" "}
              <span className="font-display italic text-indigo font-medium">you</span>{" "}
              stand.
            </h2>
            <p className="mt-4 text-muted-foreground max-w-lg">
              Create a free account, take the test once, and track your progress over time.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to={startTo}
                data-testid="landing-final-cta"
                className="neu-btn-primary group inline-flex items-center gap-2 px-8 py-4"
              >
                {startLabel}
                <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to={isAuthed ? "/dashboard" : "/login"}
                className="text-sm text-foreground/70 hover:text-indigo transition-colors"
              >
                or continue on the dashboard
              </Link>
            </div>
          </div>
        </section>

        <footer className="mt-16 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} Voxlingua · A CEFR-aligned self-assessment
        </footer>
      </div>
    </div>
  );
}

function SkillCell({ icon: Icon, name, meta, desc, testId }) {
  return (
    <div className="neu rounded-3xl p-6 flex flex-col gap-4 group cursor-default" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="neu-sm w-12 h-12 rounded-2xl flex items-center justify-center text-indigo">
          <Icon size={20} weight="duotone" />
        </div>
        <ArrowUpRight size={16} weight="bold" className="text-foreground/30 group-hover:text-indigo transition-colors" />
      </div>
      <div>
        <div className="text-xl text-foreground font-bold">{name}</div>
        <div className="overline mt-1">{meta}</div>
      </div>
      <p className="text-xs text-foreground/70 leading-relaxed">{desc}</p>
    </div>
  );
}
